import { Modal } from 'components/atoms/Modal';
import styles from './index.module.scss';
import { IconCloseTablet } from 'components/atoms/icons/IconCloseTablet';
import { useTranslation } from 'react-i18next';
import IconList2 from 'components/atoms/icons/IconList2';
import { IconChevronDown } from 'components/atoms/icons/IconChevronDown';
import { useState, useMemo } from 'react';
import IconBox from 'components/atoms/icons/IconBox';
import { normalizeNumber } from 'util/commons';

interface ConfirmSelectEmissionItemsModalProps {
  quantity: number;
  totalWeight: number;
  items: Record<
    string,
    {
      name: string;
      weight: string;
    }
  >;
  onSelectItems: (isCompleted: boolean) => void;
  onClose?: () => void;
}

export const ConfirmSelectEmissionItemsModal = ({
  quantity,
  totalWeight,
  items,
  onSelectItems,
  onClose,
}: ConfirmSelectEmissionItemsModalProps) => {
  const { t } = useTranslation();
  const [isShowAllDetails, setIsShowAllDetails] = useState(false);

  const itemWeights = useMemo(() => {
    const weights = new Map<string, number>();
    Object.entries(items).forEach(([itemId, item]) => {
      weights.set(itemId, Number(item.weight));
    });
    return weights;
  }, [items]);

  return (
    <div className={styles.customModal}>
      <Modal>
        <div className={styles.confirmSelectEmissionItemsModal}>
          <div className={styles.header}>
            <div className={styles.title}>
              {t('confirm_select_route_item.modal_confirm.title')}
              <br />
              <span className={styles.note}>
                {t('confirm_select_route_item.modal_confirm.note')}
              </span>
            </div>

            <IconCloseTablet onClick={onClose} />
          </div>

          <div className={styles.body}>
            <div className={styles.summary}>
              <div className={styles.row}>
                <div className={styles.label}>
                  {t('confirm_select_route_item.overall.quantity')}
                </div>
                <div className={`${styles.value} ${styles.quantity}`}>
                  {normalizeNumber({
                    value: quantity,
                  })}
                  <span className={styles.unit}>
                    {t('confirm_select_route_item.overall.selected_count_unit')}
                  </span>
                </div>
              </div>
              <div className={styles.row}>
                <div className={styles.label}>
                  {t('confirm_select_route_item.overall.weight')}
                </div>
                <div className={`${styles.value} ${styles.weight}`}>
                  {normalizeNumber({ value: totalWeight })}
                  <span className={styles.unit}>kg</span>
                </div>
              </div>
            </div>

            <div className={styles.itemList}>
              <div
                className={styles.itemListHeader}
                style={{
                  cursor: 'pointer',
                }}
                onClick={() => setIsShowAllDetails(!isShowAllDetails)}
              >
                <div className={styles.title}>
                  <IconList2 fill="white" />
                  <span>
                    {t('confirm_select_route_item.modal_confirm.show_details')}
                  </span>
                </div>
                <div
                  style={{
                    transform: isShowAllDetails ? 'rotate(180deg)' : 'none',
                  }}
                >
                  <IconChevronDown size={24} />
                </div>
              </div>
              {isShowAllDetails && (
                <div>
                  {Object.entries(items).map(([itemId, item]) => (
                    <div key={itemId} className={styles.item}>
                      <div className={styles.icon}>
                        <IconBox color="#86ea5c" />
                      </div>
                      <div className={styles.itemName}>{item.name}</div>
                      <div className={styles.itemWeight}>
                        {normalizeNumber({
                          value: itemWeights.get(itemId) || 0,
                        })}
                        <span className={styles.unit}>kg</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className={styles.footer}>
            <button
              className={styles.cancelBtn}
              onClick={() => onSelectItems(false)}
            >
              {t(
                'confirm_select_route_item.modal_confirm.continue_collecting_btn'
              )}
            </button>

            <button
              className={styles.confirmBtn}
              onClick={() => onSelectItems(true)}
            >
              {t(
                'confirm_select_route_item.modal_confirm.complete_collecting_btn'
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
